import { Category } from '../types/Category';
import { Content } from '../types/Content';
import axios from 'axios';
import api from './api';

/** AI suggest-tree calls hit Java + embedding; axios has no global timeout, so the UI would spin forever. */
export const SUGGEST_TREE_REQUEST_TIMEOUT_MS = 300_000;

const trimCategoryId = (id: string) => id.replace(/^\uFEFF/g, '').trim();

const categoryIdQuery = (categoryId: string) => {
  const qs = new URLSearchParams();
  qs.set('categoryId', trimCategoryId(categoryId));
  return qs.toString();
};

/** Load one category level for the content pane (direct children or content rows only). */
export const fetchCategoryForPane = async (categoryId: string): Promise<Category> => {
  const rootId = trimCategoryId(categoryId);
  const categoryResponse = await api.get<Category>(`/categories?${categoryIdQuery(rootId)}`);
  const category = categoryResponse.data;

  if (category.contentCategory) {
    const contentsResponse = await api.get(`/content/list?${categoryIdQuery(rootId)}`);
    return {
      ...category,
      contents: contentsResponse.data,
    };
  }

  const subcategoriesResponse = await api.get(`/categories/subcategories?${categoryIdQuery(rootId)}`);
  const children = (subcategoriesResponse.data as Category[]).map((subcategory) => ({
    ...subcategory,
    parentId: category.id,
  }));
  return {
    ...category,
    children,
  };
};

export const fetchCategoryWithChildren = async (categoryId: string): Promise<Category> => {
  try {
    const rootId = trimCategoryId(categoryId);
    // Explicit query string: axios params can mangle long Hebrew composite ids.
    const categoryResponse = await api.get<Category>(`/categories?${categoryIdQuery(rootId)}`);
    const category = categoryResponse.data;

    // Recursively fetch children
    const fetchChildren = async (parentCategory: Category): Promise<Category> => {
      const pid = trimCategoryId(parentCategory.id);
      // If it's a content category, fetch its contents
      if (parentCategory.contentCategory) {
        const contentsResponse = await api.get(`/content/list?${categoryIdQuery(pid)}`);
        return {
          ...parentCategory,
          contents: contentsResponse.data
        };
      }

      // Fetch subcategories
      const subcategoriesResponse = await api.get(`/categories/subcategories?${categoryIdQuery(pid)}`);
      const subcategories = subcategoriesResponse.data;
      
      if (subcategories.length === 0) {
        return parentCategory;
      }

      // Recursively fetch children for each subcategory
      const childrenPromises = subcategories.map(async (subcategory: Category) => {
        const childWithChildren = await fetchChildren(subcategory);
        return {
          ...childWithChildren,
          parentId: parentCategory.id
        };
      });

      const children = await Promise.all(childrenPromises);
      return {
        ...parentCategory,
        children,
      };
    };

    // Start the recursive fetching
    return fetchChildren(category);
  } catch (error) {
    console.error('Error fetching category with children:', error);
    throw error;
  }
};

export const updateCategoryInTree = (cats: Category[], targetId: string, updateFn: (cat: Category) => Category): Category[] => {
    return cats.map(cat => {
    if (cat.id === targetId) {
      return updateFn(cat);
    }
    if (cat.children) {
      return {
        ...cat,
            children: updateCategoryInTree(cat.children, targetId, updateFn)
          };
        }
        return cat;
      });
    };

export type AddMatchingContentMatchPreview = {
  contentId: string;
  subcase: string;
  textPreview: string;
  score?: number;
};

export type CompareContentScopeType = 'CONTENT_CATEGORY' | 'BRANCH';
export type CompareContentMatchRule = 'EQUIVALENT' | 'SUBCASE_ONLY';

export type CompareContentPreview = {
  totalInSource: number;
  matchedCount: number;
  unmatchedCount: number;
  matchedContentIds: string[];
  unmatchedContentIds: string[];
};

export const previewCompareContent = async (
  sourceCategoryId: string,
  compareScopeType: CompareContentScopeType,
  compareScopeCategoryId: string,
  matchRule: CompareContentMatchRule,
) => {
  const response = await api.post('/categories/compare-content/preview', {
    sourceCategoryId,
    compareScopeType,
    compareScopeCategoryId,
    matchRule,
  });
  const data = response.data as {
    totalInSource?: number;
    matchedCount?: number;
    unmatchedCount?: number;
    matchedContentIds?: string[];
    unmatchedContentIds?: string[];
  };
  return {
    totalInSource: data.totalInSource ?? 0,
    matchedCount: data.matchedCount ?? 0,
    unmatchedCount: data.unmatchedCount ?? 0,
    matchedContentIds: data.matchedContentIds ?? [],
    unmatchedContentIds: data.unmatchedContentIds ?? [],
  } satisfies CompareContentPreview;
};

export const previewAddMatchingContent = async (targetCategoryId: string, sourceCategoryId: string) => {
  const response = await api.post('/categories/add-matching-content/preview', {
    targetCategoryId,
    sourceCategoryId,
  });
  return response.data as {
    matchCount: number;
    matches?: AddMatchingContentMatchPreview[];
    defaultSourceCategoryId?: string;
    siblingContentCategoryIds?: string[];
  };
};

export const applyAddMatchingContent = async (
  targetCategoryId: string,
  sourceCategoryId: string,
  move: boolean,
  contentIds?: string[],
) => {
  const response = await api.post('/categories/add-matching-content', {
    targetCategoryId,
    sourceCategoryId,
    move,
    ...(contentIds && contentIds.length > 0 ? { contentIds } : {}),
  });
  return response.data as {
    transferredCount: number;
    transfers: Array<{
      contentId: string;
      sourceContentId?: string;
      sourceCategoryId: string;
      targetCategoryId: string;
      moved: boolean;
    }>;
  };
};

export type AddMatchingContentTransferPayload = {
  contentId: string;
  sourceContentId?: string;
  sourceCategoryId: string;
  targetCategoryId: string;
  moved: boolean;
};

export const mapAddMatchingContentTransfers = (
  transfers: Array<{
    contentId: string;
    sourceContentId?: string;
    sourceCategoryId: string;
    targetCategoryId: string;
    moved?: boolean;
  }>,
  move: boolean,
): AddMatchingContentTransferPayload[] =>
  transfers.map((t) => ({
    contentId: t.contentId,
    sourceContentId: t.sourceContentId,
    sourceCategoryId: t.sourceCategoryId,
    targetCategoryId: t.targetCategoryId,
    moved: move,
  }));

/** Redo add-matching: re-apply copy/move for the same source row ids from a prior transfer list. */
export const replayAddMatchingContentTransfers = async (
  targetCategoryId: string,
  sourceCategoryId: string,
  move: boolean,
  transfers: AddMatchingContentTransferPayload[],
) => {
  const sourceIds = transfers
    .map((t) => t.sourceContentId ?? t.contentId)
    .filter((id): id is string => !!id);
  if (sourceIds.length === 0) {
    return [];
  }
  const data = await applyAddMatchingContent(targetCategoryId, sourceCategoryId, move, sourceIds);
  return mapAddMatchingContentTransfers(data.transfers ?? [], move);
};

/** Replay move-mode add-matching-content for redo (move assigns a new row id each time). */
export const replayAddMatchingContentMoves = async (
  transfers: AddMatchingContentTransferPayload[],
) => {
  const updated: AddMatchingContentTransferPayload[] = [];
  for (const t of transfers) {
    const response = await api.put('/content/move', null, {
      params: { contentId: t.contentId, newParentId: t.targetCategoryId },
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    const moved = response.data as { id: string };
    updated.push({
      ...t,
      contentId: moved.id,
      sourceContentId: t.sourceContentId ?? t.contentId,
      moved: true,
    });
  }
  return updated;
};

export const collapseSubtreeToContentCategory = async (categoryId: string) => {
  const response = await api.post('/categories/collapse-subtree-to-content-category', { categoryId });
  return response.data as { categoryId: string; contentRowCount: number };
};

export const suggestTreeFromCategory = async (
  categoryId: string,
  maxDepth: number,
  context?: string,
  strategy?: 'top_down' | 'bottom_up',
) => {
  try {
    const response = await api.post(
      '/categories/suggest-tree',
      {
        categoryId,
        maxDepth,
        context: context || '',
        ...(strategy ? { strategy } : {}),
      },
      { timeout: SUGGEST_TREE_REQUEST_TIMEOUT_MS },
    );
    return response.data;
  } catch (error) {
    console.error('Error suggesting tree from category:', error);
    throw error;
  }
};

export const suggestParentStructureOverCategories = async (
  categoryIds: string[],
  maxDepth: number,
  context?: string,
  signal?: AbortSignal,
) => {
  const response = await api.post(
    '/categories/suggest-tree',
    {
      categoryIds,
      maxDepth,
      context: context || '',
    },
    { timeout: SUGGEST_TREE_REQUEST_TIMEOUT_MS, signal },
  );
  return response.data;
};

export type MergeCompareContentResult = {
  copiedCount: number;
  skippedDuplicateCount: number;
  createdContentIds: string[];
};

export const mergeCompareContent = async (params: {
  mergeIntoDocumentId: string;
  targetContentCategoryId: string;
  sourceContentIds: string[];
  mergeFromDocumentId: string;
  mergeFromReadAccessGrantId?: number;
}): Promise<MergeCompareContentResult> => {
  const response = await api.post<MergeCompareContentResult>('/categories/merge-compare-content', {
    mergeIntoDocumentId: params.mergeIntoDocumentId,
    targetContentCategoryId: params.targetContentCategoryId,
    sourceContentIds: params.sourceContentIds,
    mergeFromDocumentId: params.mergeFromReadAccessGrantId != null ? '' : params.mergeFromDocumentId,
    ...(params.mergeFromReadAccessGrantId != null
      ? { mergeFromReadAccessGrantId: params.mergeFromReadAccessGrantId }
      : {}),
  });
  return response.data;
};

export type MergeCompareCategoriesResult = {
  copiedCount: number;
  skippedDuplicateCount: number;
  createdCategoryIds: string[];
  createdBranchShellId?: string;
};

export const mergeCompareCategories = async (params: {
  mergeIntoDocumentId: string;
  targetParentCategoryId?: string;
  sourceCategoryIds: string[];
  mergeFromDocumentId: string;
  mergeFromReadAccessGrantId?: number;
  createBranchShell?: { parentCategoryId: string; name: string };
}): Promise<MergeCompareCategoriesResult> => {
  const response = await api.post<MergeCompareCategoriesResult>(
    '/categories/merge-compare-categories',
    {
      mergeIntoDocumentId: params.mergeIntoDocumentId,
      targetParentCategoryId: params.targetParentCategoryId ?? '',
      sourceCategoryIds: params.sourceCategoryIds,
      mergeFromDocumentId: params.mergeFromReadAccessGrantId != null ? '' : params.mergeFromDocumentId,
      ...(params.mergeFromReadAccessGrantId != null
        ? { mergeFromReadAccessGrantId: params.mergeFromReadAccessGrantId }
        : {}),
      ...(params.createBranchShell ? { createBranchShell: params.createBranchShell } : {}),
    },
  );
  return response.data;
};

export type MergeCompareTypeConflictDirection = 'left' | 'right';

export type MergeCompareCategorySubtreeResult = {
  applied: boolean;
  skipped: boolean;
  transfers?: { sourceCategoryId: string; createdCategoryId: string }[];
};

export const mergeCompareCategorySubtree = async (params: {
  mergeIntoDocumentId: string;
  mergeIntoCategoryId: string;
  mergeFromCategoryId: string;
  mergeFromDocumentId: string;
  mergeFromReadAccessGrantId?: number;
  treatIntoAsParent: boolean;
  resolveMissing: string;
  resolveTypeMismatch: string;
}): Promise<MergeCompareCategorySubtreeResult> => {
  const response = await api.post<MergeCompareCategorySubtreeResult>(
    '/categories/merge-compare-category-subtree',
    {
      mergeIntoDocumentId: params.mergeIntoDocumentId,
      mergeIntoCategoryId: params.mergeIntoCategoryId,
      mergeFromCategoryId: params.mergeFromCategoryId,
      mergeFromDocumentId:
        params.mergeFromReadAccessGrantId != null ? '' : params.mergeFromDocumentId,
      treatIntoAsParent: params.treatIntoAsParent,
      resolveMissing: params.resolveMissing,
      resolveTypeMismatch: params.resolveTypeMismatch,
      ...(params.mergeFromReadAccessGrantId != null
        ? { mergeFromReadAccessGrantId: params.mergeFromReadAccessGrantId }
        : {}),
    },
  );
  return response.data;
};

export const mergeCompareTypeConflict = async (params: {
  direction: MergeCompareTypeConflictDirection;
  leftDocumentId: string;
  leftCategoryId: string;
  rightDocumentId: string;
  rightCategoryId: string;
  mergeFromReadAccessGrantId?: number;
}): Promise<{ applied: boolean; direction: string }> => {
  const response = await api.post<{ applied: boolean; direction: string }>(
    '/categories/merge-compare-type-conflict',
    {
      direction: params.direction.toUpperCase(),
      leftDocumentId: params.leftDocumentId,
      leftCategoryId: params.leftCategoryId,
      rightDocumentId: params.mergeFromReadAccessGrantId != null ? '' : params.rightDocumentId,
      rightCategoryId: params.rightCategoryId,
      ...(params.mergeFromReadAccessGrantId != null
        ? { mergeFromReadAccessGrantId: params.mergeFromReadAccessGrantId }
        : {}),
    },
  );
  return response.data;
};
